import { useCallback, useMemo, useRef, useState } from 'react';

type ColumnType<T> = any; // antd ColumnsType element, keep loose to avoid import coupling

interface UseDraggableColumnsParams<T> {
  columns: ColumnType<T>[];
  /** keys 不可拖拽的列（如 操作 列） */
  disableDragKeys?: string[];
  /** 本地存储 key，不传则不持久化 */
  storageKey?: string;
}

interface DragState {
  draggingKey?: string;
  overKey?: string;
}

export function useDraggableColumns<T = unknown>({
  columns,
  disableDragKeys = [],
  storageKey,
}: UseDraggableColumnsParams<T>) {
  const initialOrder = useMemo(() => {
    const keys = columns.map((c: any) => c.key ?? c.dataIndex ?? '');
    if (!storageKey) return keys;
    try {
      const cached = localStorage.getItem(`${storageKey}::order`);
      if (!cached) return keys;
      const parsed: string[] = JSON.parse(cached);
      // 仅保留当前存在的列，新增列追加到末尾
      const set = new Set(parsed);
      const existing = parsed.filter(k => keys.includes(k));
      const newOnes = keys.filter(k => !set.has(k));
      return [...existing, ...newOnes];
    } catch {
      return keys;
    }
  }, [columns, storageKey]);

  const [order, setOrder] = useState<string[]>(initialOrder);
  const dragStateRef = useRef<DragState>({});

  const persist = useCallback((next: string[]) => {
    setOrder(next);
    if (storageKey) {
      try {
        localStorage.setItem(`${storageKey}::order`, JSON.stringify(next));
      } catch {}
    }
  }, [storageKey]);

  const indexByKey = useMemo(() => {
    const map = new Map<string, number>();
    order.forEach((k, i) => map.set(k, i));
    return map;
  }, [order]);

  const move = useCallback((fromKey: string, toKey: string) => {
    if (!fromKey || !toKey || fromKey === toKey) return;
    if (disableDragKeys.includes(fromKey) || disableDragKeys.includes(toKey)) return;
    const current = [...order];
    const fromIdx = current.indexOf(fromKey);
    const toIdx = current.indexOf(toKey);
    if (fromIdx < 0 || toIdx < 0) return;
    current.splice(fromIdx, 1);
    current.splice(toIdx, 0, fromKey);
    persist(current);
  }, [order, disableDragKeys, persist]);

  const draggableHeaderCell = useCallback((col: any) => {
    const key = col.key ?? col.dataIndex ?? '';
    const disabled = !key || disableDragKeys.includes(key);

    return {
      draggable: !disabled,
      onDragStart: (e: React.DragEvent) => {
        if (disabled) return;
        dragStateRef.current.draggingKey = key;
        // for Firefox
        try { e.dataTransfer.setData('text/plain', key); } catch {}
        e.dataTransfer.effectAllowed = 'move';
      },
      onDragOver: (e: React.DragEvent) => {
        if (disabled) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        dragStateRef.current.overKey = key;
      },
      onDrop: (e: React.DragEvent) => {
        if (disabled) return;
        e.preventDefault();
        const fromKey = dragStateRef.current.draggingKey!;
        const toKey = key;
        move(fromKey, toKey);
        dragStateRef.current = {};
      },
    } as React.HTMLAttributes<any>;
  }, [disableDragKeys, move]);

  const orderedColumns: ColumnType<T>[] = useMemo(() => {
    const keyToCol = new Map<string, any>();
    columns.forEach((c: any) => {
      const k = c.key ?? c.dataIndex ?? '';
      keyToCol.set(k, c);
    });

    const sorted = order
      .map(k => keyToCol.get(k))
      .filter(Boolean);

    // 添加 onHeaderCell 以支持拖拽
    return sorted.map((c: any) => ({
      ...c,
      onHeaderCell: (col: any) => ({
        ...(typeof c.onHeaderCell === 'function' ? c.onHeaderCell(col) : {}),
        ...draggableHeaderCell(c),
        style: {
          cursor: disableDragKeys.includes((c.key ?? c.dataIndex ?? '') as string) ? 'default' : 'move',
          ...(c.onHeaderCell && typeof c.onHeaderCell === 'function' ? (c.onHeaderCell(col)?.style || {}) : {}),
        },
      }),
    }));
  }, [columns, order, draggableHeaderCell, disableDragKeys]);

  // 当外部 columns 变化（例如显示/隐藏列），更新顺序
  const mergedColumns = useMemo(() => {
    const currentKeys = columns.map((c: any) => c.key ?? c.dataIndex ?? '');
    const set = new Set(order);
    const merged = [...order.filter(k => currentKeys.includes(k)), ...currentKeys.filter(k => !set.has(k))];
    if (merged.length !== order.length || merged.some((k, i) => k !== order[i])) {
      persist(merged);
      return orderedColumns;
    }
    return orderedColumns;
  }, [columns, order, orderedColumns, persist]);

  return {
    columns: mergedColumns,
    order,
    setOrder: persist,
    indexByKey,
  } as const;
}

export default useDraggableColumns;


